import { createAuthClient } from "@atsready/shared";
import { API_BASE_URL } from "@/config/env";

/**
 * Admin's one auth client (T15, U16). It lives here rather than in `AuthContext.tsx`, which would
 * gain a sixth `only-export-components` warning against lint's `--max-warnings 14`.
 */
export const ADMIN_SESSION_KEY = "has_admin_session";

function readAccessToken(body: unknown): string | null {
  if (typeof body !== "object" || body === null || !("data" in body)) return null;
  const data: unknown = body.data;
  if (typeof data !== "object" || data === null || !("accessToken" in data)) return null;
  const token: unknown = data.accessToken;
  return typeof token === "string" && token.length > 0 ? token : null;
}

/**
 * The injected `refreshFn`. It posts where `refresh()` already does, so admin keeps one refresh
 * endpoint. Null means the server ended the session (401/403, or a 200 without a token), which
 * `refreshToken()` turns into `invalid-session`; a throw means a transient failure (any other
 * status, a network error), which keeps the session flag.
 */
async function refreshAdminTokens(): Promise<{ accessToken: string } | null> {
  const response = await fetch(`${API_BASE_URL}/auth/refresh`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({}),
  });
  if (response.status === 401 || response.status === 403) return null;
  if (!response.ok) throw new Error(`refresh_failed_${response.status}`);
  const accessToken = readAccessToken(await response.json());
  return accessToken === null ? null : { accessToken };
}

export const adminAuthClient = createAuthClient({
  apiBaseUrl: API_BASE_URL,
  sessionKey: ADMIN_SESSION_KEY,
  refreshFn: refreshAdminTokens,
});

/**
 * The api client's `refreshToken` hook. The shared client is single-flight, which matters: api
 * rotation is atomic, so the loser of two concurrent refreshes would get a 401 and log the winner
 * out, and `/auth/refresh` is IP-limited at 20 a minute.
 */
export async function refreshAccessToken(): Promise<string | null> {
  const { status } = await adminAuthClient.refreshToken();
  return status === "refreshed" ? adminAuthClient.getAccessToken() : null;
}
